/**
 * useAnalytics — hook för att samla in anonymiserad användningsdata.
 *
 * Tre nivåer av events:
 * - profile: skickas EN GÅNG per session (vid mount, när data finns)
 * - scenario: skickas vid varje konvergerad simulering (debounced)
 * - session_end: skickas via sendBeacon vid unload eller efter inaktivitet
 *
 * Pseudonymisering: tibberHomeId skickas i klartext till backend som
 * hashar det med ett server-side salt. Klienten kan aldrig återskapa
 * pseudonymen själv.
 *
 * Batching: events buffras lokalt och skickas i batches (var 30:e sek
 * eller när bufferten innehåller 10+ events).
 */

import { useEffect, useRef, useCallback } from "react";
import { getApiUrl } from "../api/client";
import type { AnalyticsEvent } from "../types/analytics";

// Backend-URL resolveras via samma hostname-baserade logik som övriga
// API-anrop. Tidigare hade useAnalytics en egen const som läste fel
// env-var (VITE_API_BASE_URL fanns inte satt) och skickade allt till
// localhost:3000 i prod. Silent fail eftersom analytics medvetet inte
// rapporterar fel.
const API_BASE = getApiUrl();
const BATCH_INTERVAL_MS = 30_000;
const BATCH_SIZE_LIMIT = 10;
const SESSION_STORAGE_KEY = "vibeorg_session_id";

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  }
  return id;
}

interface AnalyticsState {
  sessionStartMs: number;
  scenariosCount: number;
  lastScenarioResult: {
    paybackYears: number | null;
    annualSavings: number | null;
  };
  hasSentProfile: boolean;
  buffer: AnalyticsEvent[];
}

export function useAnalytics(tibberHomeId: string | null) {
  const sessionId = useRef<string>(getSessionId());
  const state = useRef<AnalyticsState>({
    sessionStartMs: Date.now(),
    scenariosCount: 0,
    lastScenarioResult: { paybackYears: null, annualSavings: null },
    hasSentProfile: false,
    buffer: [],
  });

  // Engagement-tracking (uppdaterat utifrån)
  const engagement = useRef({
    openedDecomposition: false,
    openedTiming: false,
    openedSoftValues: false,
    addedInvestmentPlans: false,
  });

  const flushBuffer = useCallback(
    (useBeacon = false) => {
      if (state.current.buffer.length === 0) return;
      const events = state.current.buffer;
      state.current.buffer = [];

      const payload = JSON.stringify({
        events,
        tibberHomeId,
      });

      if (useBeacon && navigator.sendBeacon) {
        // Beacon API tillåter att skicka data efter unload
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(`${API_BASE}/api/analytics/events`, blob);
      } else {
        // Vanlig fetch — fire and forget, vi bryr oss inte om responsen
        fetch(`${API_BASE}/api/analytics/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
          credentials: "omit",
        }).catch(() => {
          // Silent fail — analytics ska aldrig krasha appen
        });
      }
    },
    [tibberHomeId],
  );

  const enqueue = useCallback(
    (event: AnalyticsEvent) => {
      state.current.buffer.push(event);
      if (state.current.buffer.length >= BATCH_SIZE_LIMIT) {
        flushBuffer();
      }
    },
    [flushBuffer],
  );

  // Periodic flush
  useEffect(() => {
    const timer = setInterval(() => flushBuffer(), BATCH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [flushBuffer]);

  // Flush + session_end vid unload
  useEffect(() => {
    const handleUnload = () => {
      const sessionEnd: AnalyticsEvent = {
        type: "session_end",
        sessionId: sessionId.current,
        userPseudonym: null, // sätts serverside
        clientTimestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        productVertical: "battery",
        sessionDurationSeconds: Math.round(
          (Date.now() - state.current.sessionStartMs) / 1000,
        ),
        scenariosEvaluated: state.current.scenariosCount,
        finalPaybackYears: state.current.lastScenarioResult.paybackYears,
        finalAnnualSavings: state.current.lastScenarioResult.annualSavings,
        openedDecomposition: engagement.current.openedDecomposition,
        openedTiming: engagement.current.openedTiming,
        openedSoftValues: engagement.current.openedSoftValues,
        addedInvestmentPlans: engagement.current.addedInvestmentPlans,
      };
      state.current.buffer.push(sessionEnd);
      flushBuffer(true);
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [flushBuffer]);

  /**
   * Skickar profile-event en gång per session.
   */
  const trackProfile = useCallback(
    (profile: {
      dataSource: "tibber" | "csv_upload" | "wizard";
      priceZone: string;
      fuseSizeAmp: number;
      annualImportKWh: number;
      annualExportKWh: number;
      monthlyImportsKWh: number[];
      monthlyHourlyShape: number[][];
      monthCount: number;
      postalCodePrefix?: string;
    }) => {
      if (state.current.hasSentProfile) return;
      state.current.hasSentProfile = true;

      enqueue({
        type: "profile",
        sessionId: sessionId.current,
        userPseudonym: null,
        clientTimestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        productVertical: "battery",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dataSource: profile.dataSource,
        priceZone: profile.priceZone,
        fuseSizeAmp: profile.fuseSizeAmp,
        annualImportKWh: profile.annualImportKWh,
        annualExportKWh: profile.annualExportKWh,
        hasSolar: profile.annualExportKWh > 100,
        monthlyImportsKWh: profile.monthlyImportsKWh,
        monthlyHourlyShape: profile.monthlyHourlyShape,
        monthCount: profile.monthCount,
        ...(profile.postalCodePrefix && {
          postalCodePrefix: profile.postalCodePrefix,
        }),
      });
    },
    [enqueue],
  );

  /**
   * Skickar scenario-event vid konvergerad beräkning.
   */
  const trackScenario = useCallback(
    (scenario: {
      batteryCapacityKWh: number;
      batteryMaxPowerKW: number;
      batteryInvestmentSekBucket: number;
      strategy: string;
      ancillaryEnabled: boolean;
      powerTariffEnabled: boolean;
      lifetimePreset: string;
      investmentPlanTypes: string[];
      annualSavingsSek: number;
      paybackYears: number;
      totalLifetimeSavingsSek: number;
      totalRoiPercent: number;
      timingScore: number;
      sawWinterInsight: boolean;
    }) => {
      state.current.scenariosCount += 1;
      state.current.lastScenarioResult = {
        paybackYears: scenario.paybackYears,
        annualSavings: scenario.annualSavingsSek,
      };

      enqueue({
        type: "scenario",
        sessionId: sessionId.current,
        userPseudonym: null,
        clientTimestamp: new Date().toISOString(),
        productVertical: "battery",
        ...scenario,
      });
    },
    [enqueue],
  );

  /**
   * Markera att en panel har öppnats (engagemang-tracking)
   */
  const markEngagement = useCallback(
    (key: keyof typeof engagement.current) => {
      engagement.current[key] = true;
    },
    [],
  );

  /**
   * Funnel-event för wizarden: started/abandoned/completed.
   * För att förstå var användare faller bort.
   */
  const trackWizardFunnel = useCallback(
    (step: "started" | "abandoned" | "completed", durationMs?: number) => {
      enqueue({
        type: "wizard_funnel",
        sessionId: sessionId.current,
        userPseudonym: null,
        clientTimestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        productVertical: "battery",
        step,
        ...(durationMs !== undefined && { durationMs }),
      });
    },
    [enqueue],
  );

  return { trackProfile, trackScenario, markEngagement, trackWizardFunnel };
}
